import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircle, ChevronRight, X } from 'lucide-react';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useProfile } from '@/hooks/useProfile';

const ProfileCompletionCard: React.FC = () => {
  const [dismissed, setDismissed] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [missingItems, setMissingItems] = useState<string[]>([]);
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('profile_completion_dismissed_until');
    if (dismissedUntil && parseInt(dismissedUntil) > Date.now()) {
      setDismissed(true);
    }
  }, []);
  
  useEffect(() => {
    if (!profile || isLoading) return;
    
    const missing: string[] = [];
    let completed = 0;
    const total = 5; // picture, sports, job, club, social links
    
    // 1) Profile picture
    if (profile.avatar_url) {
      completed++;
    } else {
      missing.push('profile picture');
    }
    
    // 2) Sports
    if (profile.user_sports?.length > 0) {
      completed++;
    } else {
      missing.push('sports preferences');
    }
    
    // 3) Job information
    if (profile.job_type && profile.company) {
      completed++;
    } else {
      missing.push('job information');
    }
    
    // 4) Club membership — ANY membership row OR custom club
    if (
      profile.club_id ||
      (profile.user_clubs?.length > 0) ||
      profile.custom_club
    ) {
      completed++;
    } else {
      missing.push('club membership');
    }
    
    // 5) Social links
    if (profile.linkedin_url || profile.facebook_url || profile.instagram_url) {
      completed++;
    } else {
      missing.push('social links');
    }
    
    setCompletionPercentage(Math.round((completed / total) * 100));
    setMissingItems(missing);
    
    // auto-dismiss when complete
    if (completed === total) {
      setTimeout(() => setDismissed(true), 100);
    }
  }, [profile, isLoading]);
  
  const handleDismiss = () => {
    const dismissUntil = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('profile_completion_dismissed_until', dismissUntil.toString());
    setDismissed(true);
  };
  
  const handleCompleteProfile = () => {
    navigate('/dashboard/profile');
  };
  
  if (dismissed || completionPercentage === 100) return null;
  
  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 relative">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 bg-dark-300/80 hover:bg-dark-400 rounded-full text-dark-800 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      
      <div className="flex items-start space-x-4">
        <div className="bg-dark-300 p-3 rounded-full">
          <UserCircle className="h-6 w-6 text-gold-400" />
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">Complete Your Profile</h3>
          
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-dark-800">Profile Completion</span>
              <span className="text-gold-400 font-medium">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="h-1.5 bg-dark-300" />
          </div>
          
          {missingItems.length > 0 && (
            <div className="mb-4">
              <p className="text-dark-800 text-sm mb-2">Add the following to complete your profile:</p>
              <ul className="space-y-1">
                {missingItems.slice(0, 3).map((item, index) => (
                  <li key={index} className="flex items-center text-sm text-white">
                    <div className="w-4 h-4 mr-2 rounded-full border border-dark-400 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-gold-400 rounded-full" />
                    </div>
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </li>
                ))}
                {missingItems.length > 3 && (
                  <li className="text-sm text-dark-800 ml-6">
                    +{missingItems.length - 3} more items
                  </li>
                )}
              </ul>
            </div>
          )}
          
          <Button
            onClick={handleCompleteProfile}
            className="w-full bg-gold-400 hover:bg-gold-500 text-dark-50 flex items-center justify-center"
          >
            Complete Profile
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionCard;
